import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Chip,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CodeIcon from "@material-ui/icons/Code";
import api from "../../services/api";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  variablesBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "rgba(115, 103, 240, 0.08)" : "rgba(115, 103, 240, 0.05)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(115, 103, 240, 0.25)" : "rgba(115, 103, 240, 0.2)"}`,
  },
  chip: {
    margin: theme.spacing(0.5),
    cursor: "pointer",
    fontFamily: "monospace",
    fontWeight: 600,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}33`,
    },
  },
}));

const FlowBuilderAddFormModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [message, setMessage] = useState(
    i18n.t("flowBuilder.editor.nodes.form.defaultMessage") || "Por favor, completa el siguiente formulario: {link}"
  );

  useEffect(() => {
    if (open) {
      fetchForms();
      if (data) {
        setSelectedFormId(data.data?.formId || "");
        setMessage(
          data.data?.message ||
          i18n.t("flowBuilder.editor.nodes.form.defaultMessage") ||
          "Por favor, completa el siguiente formulario: {link}"
        );
      }
    }
  }, [open, data]);

  const fetchForms = async () => {
    try {
      const response = await api.get("/forms");
      setForms(response.data);
    } catch (err) {
      toast.error(i18n.t("flowBuilder.editor.nodes.form.errorLoad") || "Error al cargar los formularios");
    }
  };

  const selectedForm = useMemo(() => {
    return forms.find((f) => f.id === selectedFormId);
  }, [forms, selectedFormId]);

  const formFields = useMemo(() => {
    if (!selectedForm || !selectedForm.fields) return [];
    try {
      const parsed = typeof selectedForm.fields === "string" ? JSON.parse(selectedForm.fields) : selectedForm.fields;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }, [selectedForm]);

  const handleCopyVariable = (varName) => {
    const tag = `{{${varName}}}`;
    navigator.clipboard.writeText(tag);
    toast.info(
      i18n.t("flowBuilder.editor.nodes.form.copiedVariable", { variable: tag }) ||
      `Variable ${tag} copiada al portapapeles`
    );
  };

  const handleSave = () => {
    if (!selectedFormId) {
      return toast.error(i18n.t("flowBuilder.editor.nodes.form.errorSelect") || "Por favor, selecciona un formulario");
    }

    if (data && data.id) {
      onUpdate({
        ...data,
        data: {
          ...data.data,
          formId: selectedFormId,
          formSlug: selectedForm?.slug,
          formName: selectedForm?.name,
          message
        }
      });
    } else {
      onSave({
        formId: selectedFormId,
        formSlug: selectedForm?.slug,
        formName: selectedForm?.name,
        message
      });
    }
    close();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{i18n.t("flowBuilder.editor.nodes.form.configTitle") || "Configurar Formulario Dinámico"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {i18n.t("flowBuilder.editor.nodes.form.configDesc") || "El cliente recibirá un enlace para llenar el formulario en la web. Una vez enviado, el flujo continuará."}
        </Typography>
        <FormControl fullWidth variant="outlined" margin="normal">
          <InputLabel>{i18n.t("flowBuilder.editor.nodes.form.selectForm") || "Seleccionar Formulario"}</InputLabel>
          <Select
            value={selectedFormId}
            onChange={(e) => setSelectedFormId(e.target.value)}
            label={i18n.t("flowBuilder.editor.nodes.form.selectForm") || "Seleccionar Formulario"}
          >
            {forms.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.title ? `${f.title} (${f.name})` : f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedForm && formFields.length > 0 && (
          <div className={classes.variablesBox}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <CodeIcon fontSize="small" style={{ color: "#7367F0", marginRight: 6 }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {i18n.t("flowBuilder.editor.nodes.form.availableVariablesTitle") || "Variables Capturadas por este Formulario"}
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
              {i18n.t("flowBuilder.editor.nodes.form.availableVariablesDesc") ||
                "Haz clic en una variable para copiarla. Podrás usarla en los siguientes nodos del flujo (Texto, Mensaje, Condición, etc.):"}
            </Typography>
            <Box display="flex" flexWrap="wrap">
              {formFields.map((f, idx) => (
                <Tooltip key={idx} title={`${f.label || f.name} (${f.type || "texto"}) - Clic para copiar`}>
                  <Chip
                    icon={<FileCopyIcon style={{ fontSize: 14 }} />}
                    label={`{{${f.name}}}`}
                    size="small"
                    className={classes.chip}
                    onClick={() => handleCopyVariable(f.name)}
                  />
                </Tooltip>
              ))}
            </Box>
          </div>
        )}

        <TextField
          label={i18n.t("flowBuilder.editor.nodes.form.messageLabel") || "Mensaje para enviar (usa {link} para la URL)"}
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} className={classes.secondaryButton}>
          {i18n.t("contactModal.buttons.cancel") || "Cancelar"}
        </Button>
        <Button onClick={handleSave} className={classes.applyButton}>
          {i18n.t("common.save") || "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddFormModal;
